import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import React, { useRef, useState } from 'react';
import firebaseErrorMessages from '../../../components/errorMessages/firebase';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import SuccessFormMessage from '../../../components/messages/SuccessFormMessage';
import { postEnableUsers } from '../../../services/admin/UsersService';

type EnableUsersButtonProps = {
  selectedUsers: string[];
  afterEnabling: () => void;
};

const EnableUsersButton = ({
  selectedUsers,
  afterEnabling,
}: EnableUsersButtonProps) => {
  const [visible, setVisible] = useState(false);
  const [amountDeleted, setAmountDeleted] = useState(0);
  const toast = useRef<Toast>(null);

  const { mutate, isLoading, isSuccess } = useMutation({
    mutationFn: (data: string[]) => {
      return postEnableUsers(data);
    },
    onSuccess: () => {
      setAmountDeleted(selectedUsers.length);
      afterEnabling();
    },

    onError: (error: AxiosError) => {
      setVisible(false);
      const errorData = error.response?.data as { code: string };
      showToast(
        'error',
        toast,
        'Enable Users Error',
        firebaseErrorMessages[errorData?.code] ||
          'An error occurred! please try again later',
        3000
      );
    },
  });

  const requestEnableUsers = (users: string[]) => {
    const length = users.length;
    confirmDialog({
      message: `Do you want to enable ${length} ${
        length > 1 ? 'users' : 'user'
      }?`,
      header: 'Enable Confirmation',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        mutate(users);
        setVisible(true);
      },
    });
  };

  return (
    <React.Fragment>
      <Button
        className="h-fit w-fit py-3 px-3 bg-green-500 border-0 text-xl radius-10px"
        disabled={selectedUsers.length === 0}
        onClick={() => requestEnableUsers(selectedUsers)}
      >
        Enable Users
      </Button>
      <ConfirmDialog />
      <Dialog
        header="Enable in progress"
        visible={visible}
        onHide={() => setVisible(false)}
        className="w-fit"
        contentClassName="flex justify-content-center"
      >
        {isLoading && <ProgressSpinner className="mx-auto" />}
        {isSuccess && (
          <SuccessFormMessage
            message={`The ${amountDeleted} ${
              amountDeleted > 1 ? 'users have' : 'user has'
            } been enabled!`}
          />
        )}
      </Dialog>
      <ErrorToast toastRef={toast} />
    </React.Fragment>
  );
};

export default EnableUsersButton;
